import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import Footer from '../components/Footer';
import { formatCurrencyInput } from '../utils/currencyUtils';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc
} from 'firebase/firestore';
import { Link } from 'react-router-dom';
import ListingThumb from '../components/ListingThumb';
import { placeholderFor } from '../utils/listingPlaceholder';
import { offerModeOf, OFFER_MODE_LABELS } from '../utils/offerMode';

// Filter pills shared with the Wantlist page (WantList.jsx): label inside a
// gold-bordered pill, borderless control filling the rest. Kept local the
// way WantList keeps its copy.
const PEDAL_FILTER_FIELD_STYLE = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.65rem',
  width: '100%',
  borderRadius: '25px',
  border: '1px solid #C89F3C',
  backgroundColor: 'white',
  padding: '0.65rem 1rem 0.65rem 1.1rem',
  boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.1)',
  boxSizing: 'border-box',
};
const PEDAL_FILTER_LABEL_STYLE = {
  fontSize: '0.9rem',
  fontWeight: 600,
  color: '#4A5759',
  fontFamily: 'Nunito, sans-serif',
  whiteSpace: 'nowrap',
  flexShrink: 0,
};
const PEDAL_FILTER_CONTROL_BASE = {
  fontSize: '0.9375rem',
  fontFamily: 'Nunito, sans-serif',
  fontWeight: 400,
  color: '#374151',
  width: '100%',
  flex: 1,
  minWidth: 0,
  border: 'none',
  outline: 'none',
  padding: '0.15rem 0',
  backgroundColor: 'transparent',
  boxSizing: 'border-box',
};
const PEDAL_FILTER_SELECT_STYLE = {
  ...PEDAL_FILTER_CONTROL_BASE,
  cursor: 'pointer',
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  paddingRight: '2rem',
  backgroundImage: "url(\"data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23C89F3C' d='M6 8L0 0h12z'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 0.25rem center',
};
const PEDAL_FILTER_INPUT_STYLE = {
  ...PEDAL_FILTER_CONTROL_BASE,
  MozAppearance: 'textfield',
};

// Session-storage key for the scroll snapshot. Opening a pedal and coming back
// (including via the iOS back-swipe) has to land where you were rather than at
// the top of someone else's collection. Mirrors the pattern in Matches /
// SearchResults / LatestPedals: the position is saved, the listings are not,
// since they are refetched on mount either way.
const PEDALBOARD_STORAGE_KEY = 'publicPedalboard_scrollState';

const PublicPedalboard = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const hasRestoredScrollRef = useRef(false);

  // Read the snapshot once, at mount, before any fetch can overwrite the page.
  // Scoped to this pedalboard and to the last half hour: restoring a position
  // captured against a different (or long-since-changed) collection would drop
  // the user somewhere arbitrary, which is worse than starting at the top.
  const [savedScrollState] = useState(() => {
    try {
      const raw = sessionStorage.getItem(PEDALBOARD_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed.userId !== userId) return null;
      if (Date.now() - parsed.timestamp > 30 * 60 * 1000) return null;
      return parsed;
    } catch (err) {
      return null;
    }
  });

  const DEFAULT_FILTERS = { brand: 'all', minPrice: '', maxPrice: '' };

  // Restored alongside the scroll offset, not just remembered for its own sake:
  // this page filters during render, so coming back to default filters would
  // apply a saved offset to a longer list than the one the offset was measured
  // against and land the user on unrelated pedals.
  const [filters, setFilters] = useState(savedScrollState?.filters || DEFAULT_FILTERS);

  // Function to format price input to max 2 decimal places
  const applyFilters = (pedals) => {
    return pedals.filter((pedal) => {
      // Only show active pedals that are not in trade or need reactivation
      if (!pedal.active || pedal.inTrade || pedal.needsReactivation) return false;
      
      if (filters.brand !== 'all' && pedal.make !== filters.brand) return false;
      if (filters.minPrice && Number(pedal.price) < Number(filters.minPrice)) return false;
      if (filters.maxPrice && Number(pedal.price) > Number(filters.maxPrice)) return false;
      return true;
    });
  };

  // Get unique brands from the pedals
  const getAvailableBrands = () => {
    const brands = new Set();
    listings.forEach(pedal => {
      // Only include brands from active pedals that are not in trade or need reactivation
      if (pedal.active && !pedal.inTrade && !pedal.needsReactivation && pedal.make) {
        brands.add(pedal.make);
      }
    });
    return Array.from(brands).sort();
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        // PII isolation: read sanitized public mirror.
        const userRef = doc(db, 'publicProfiles', userId);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const data = userSnap.data();
          
          // 🚫 CHECK IF USER IS BANNED - Don't show their pedalboard
          if (data.isBanned === true) {
            console.log('🚫 User is banned - not showing pedalboard');
            setUserData(null); // Treat as user not found
            setLoading(false);
            return;
          }
          
          setUserData({
            username: data.username || 'Unknown User',
            photoURL: data.photoURL || '/images/default-avatar.png'
          });
        } else {
          setUserData(null);
        }
      } catch (err) {
        console.error('Failed to load user data:', err);
        setUserData(null);
      }
    };

    const fetchPedals = async () => {
      try {
        setLoading(true);
        const startTime = performance.now();
        console.log(`⏱️ Starting pedal fetch for user: ${userId}`);
        
        const q = query(
          collection(db, 'listings'),
          where('ownerId', '==', userId)
        );
        
        const snapshot = await getDocs(q);
        const pedalsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
        
        const endTime = performance.now();
        console.log(`✅ Fetched ${pedalsData.length} pedals in ${(endTime - startTime).toFixed(0)}ms`);
        
        setListings(pedalsData);
      } catch (err) {
        console.error('Failed to load pedals:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
    fetchPedals();
  }, [userId]);

  const saveScrollState = () => {
    sessionStorage.setItem(PEDALBOARD_STORAGE_KEY, JSON.stringify({
      userId,
      scrollY: window.scrollY,
      filters,
      timestamp: Date.now()
    }));
  };

  // Safety net for refreshes and any navigation that doesn't go through the
  // card links. Guarded on having listings so an early unload can't overwrite a
  // good snapshot with scroll 0.
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (listings.length > 0) saveScrollState();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listings, userId]);

  // Restore only after the grid exists. This page refetches on every mount and
  // hides the whole list behind a loading gate, so at mount there is nothing to
  // scroll to — scrolling then would silently do nothing. The ref keeps it to
  // once per mount, so a later filter change doesn't yank the user back.
  useEffect(() => {
    if (hasRestoredScrollRef.current) return;
    if (!savedScrollState) return;
    if (loading) return;
    if (listings.length === 0) return;
    hasRestoredScrollRef.current = true;
    // Cards declare a fixed aspect ratio, so the grid's height is settled before
    // the images finish loading; one tick is enough for layout.
    setTimeout(() => window.scrollTo(0, savedScrollState.scrollY || 0), 50);
  }, [loading, listings, savedScrollState]);

  // Drop the snapshot when the user leaves the pedalboard/listing flow, so
  // arriving here fresh weeks later doesn't restore a stale position. The
  // cleanup runs after the route has changed, so window.location is the
  // destination.
  useEffect(() => {
    return () => {
      const next = window.location.pathname;
      const stayingInFlow = next.startsWith('/listing/') || next.startsWith('/pedalboard/');
      if (!stayingInFlow) sessionStorage.removeItem(PEDALBOARD_STORAGE_KEY);
    };
  }, []);

  if (loading) {
    return (
      <div style={{ backgroundColor: '#F7F3EB', minHeight: '100vh', paddingTop: '2rem', textAlign: 'center' }}>
        <p>Loading pedalboard...</p>
      </div>
    );
  }

  if (!userData) {
    return (
      <div style={{ backgroundColor: '#F7F3EB', minHeight: '100vh', paddingTop: '2rem', textAlign: 'center' }}>
        <p>User not found</p>
        <button
          onClick={() => navigate(-1)}
          style={{
            marginTop: '1rem',
            backgroundColor: '#5A9ECB',
            color: 'white',
            border: 'none',
            borderRadius: '9999px',
            padding: '8px 20px',
            fontFamily: 'Nunito, sans-serif',
            fontWeight: 'bold',
            cursor: 'pointer'
          }}
        >
          Go Back
        </button>
      </div>
    );
  }

  const filteredPedals = applyFilters(listings);

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'transparent', minHeight: 'auto', display: 'flex', flexDirection: 'column' }}>
      {/* Yellow background section */}
      <div style={{ backgroundColor: '#F7F3EB', flex: 1 }}>
        
        {/* Header */}
        <div style={{ textAlign: 'center', paddingTop: '2rem', paddingBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
            <img
              src={userData.photoURL}
              alt={userData.username}
              style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                objectFit: 'cover',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
              }}
            />
            <h2 style={{ fontSize: '2rem', fontWeight: 'bold', fontFamily: 'Nunito, sans-serif', margin: 0 }}>
              {userData.username}'s Pedalboard
            </h2>
          </div>
          <Link
            to={`/profile/${userId}`}
            style={{
              marginTop: '0.25rem',
              display: 'inline-block',
              color: '#5A9ECB',
              fontFamily: 'Nunito, sans-serif',
              fontSize: '17px',
              textDecoration: 'underline'
            }}
            onMouseEnter={(e) => e.target.style.opacity = '0.7'}
            onMouseLeave={(e) => e.target.style.opacity = '1'}
          >
            View Profile
          </Link>
        </div>

        {filteredPedals.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p style={{ fontSize: '1.125rem', color: '#6b7280', fontFamily: 'Nunito, sans-serif' }}>
              No active pedals to display
            </p>
          </div>
        ) : (
          <>
            {/* Filters: same pill fields as the Wantlist page (owner, 2026-11-01);
                the old white card with a label/select grid is gone. */}
            <h2 style={{ maxWidth: '1400px', margin: '1rem auto 0.5rem', padding: '0 2rem', color: '#4A5759', fontFamily: 'Nunito', fontSize: '1.25rem', fontWeight: '600' }} className="public-pedalboard-filter-heading">Filter Pedals</h2>
            <div
              className="public-pedalboard-filters"
              style={{ maxWidth: '1400px', margin: '0 auto 1.5rem', padding: '0 2rem', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', columnGap: '1.25rem', rowGap: '1rem', boxSizing: 'border-box' }}
            >
              <style jsx>{`
                @media (max-width: 768px) {
                  .public-pedalboard-filter-heading {
                    padding: 0 0.5rem !important;
                  }
                  .public-pedalboard-filters {
                    grid-template-columns: 1fr !important;
                    column-gap: 0 !important;
                    row-gap: 0.75rem !important;
                    padding: 0 0.5rem !important;
                  }
                }
                .public-pedalboard-filters input[type="number"]::-webkit-outer-spin-button,
                .public-pedalboard-filters input[type="number"]::-webkit-inner-spin-button {
                  -webkit-appearance: none;
                  margin: 0;
                }
              `}</style>
              <div style={PEDAL_FILTER_FIELD_STYLE}>
                <span style={PEDAL_FILTER_LABEL_STYLE}>Brand</span>
                <select
                  value={filters.brand}
                  onChange={(e) => { setFilters({ ...filters, brand: e.target.value }); e.target.blur(); }}
                  style={PEDAL_FILTER_SELECT_STYLE}
                  aria-label="Filter by brand"
                >
                  <option value='all'></option>
                  {getAvailableBrands().map(brand => (
                    <option key={brand} value={brand}>{brand}</option>
                  ))}
                </select>
              </div>
              <div style={PEDAL_FILTER_FIELD_STYLE}>
                <span style={PEDAL_FILTER_LABEL_STYLE}>Min Price:</span>
                <input
                  type='number'
                  inputMode='numeric'
                  value={filters.minPrice}
                  onChange={(e) => setFilters({ ...filters, minPrice: formatCurrencyInput(e.target.value) })}
                  style={PEDAL_FILTER_INPUT_STYLE}
                  aria-label="Minimum price"
                />
              </div>
              <div style={PEDAL_FILTER_FIELD_STYLE}>
                <span style={PEDAL_FILTER_LABEL_STYLE}>Max Price:</span>
                <input
                  type='number'
                  inputMode='numeric'
                  value={filters.maxPrice}
                  onChange={(e) => setFilters({ ...filters, maxPrice: formatCurrencyInput(e.target.value) })}
                  style={PEDAL_FILTER_INPUT_STYLE}
                  aria-label="Maximum price"
                />
              </div>
            </div>

            {/* Pedal Grid */}
            <div className="pedalboard-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.5rem', maxWidth: '1400px', margin: '1rem auto', padding: '0 2rem' }}>
              <style jsx>{`
                @media (max-width: 1200px) {
                  .pedalboard-grid {
                    grid-template-columns: repeat(3, 1fr) !important;
                  }
                }
                @media (max-width: 900px) {
                  .pedalboard-grid {
                    grid-template-columns: repeat(2, 1fr) !important;
                  }
                }
                @media (max-width: 768px) {
                  .pedalboard-grid {
                    grid-template-columns: 1fr !important;
                    margin: 1rem 0.5rem !important;
                    padding: 0 !important;
                  }
                }
              `}</style>
              {filteredPedals.map((pedal) => (
                <div key={pedal.id} style={{ 
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)', 
                  borderRadius: '0.75rem', 
                  padding: '1.5rem',
                  backgroundColor: 'white',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}>
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ marginBottom: '0.25rem' }}>
                      <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#374151', lineHeight: '1.1' }}>
                        {pedal.make}
                      </div>
                      <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#374151', lineHeight: '1.1' }}>
                        {pedal.model}
                      </div>
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#6b7280', lineHeight: '1.5' }}>
                      <div><strong>Condition:</strong> {pedal.condition}</div>
                      <div><strong>Price:</strong> ${pedal.price}</div>
                      <div><strong>Offers:</strong> {OFFER_MODE_LABELS[offerModeOf(pedal)]}</div>
                      {pedal.color && <div><strong>Color:</strong> {pedal.color}</div>}
                      <div><strong>Status: </strong> 
                        <span style={{ 
                          color: pedal.inTrade ? '#ea8c00' : 
                                 (pedal.active ? '#166534' : '#dc2626'),
                          fontWeight: '500'
                        }}>
                          {pedal.inTrade ? (pedal.purchaseOfferId ? 'In Purchase' : 'In Trade') :
                           (pedal.active ? 'Active' : 'Inactive')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {(pedal.images?.top || placeholderFor(pedal).url) && (
                    <div style={{ marginBottom: '1rem', textAlign: 'center' }}>
                      <Link to={`/listing/${pedal.id}`} onClick={saveScrollState} style={{ display: 'inline-block' }}>
                        <ListingThumb
                          src={pedal.images?.top || placeholderFor(pedal).url}
                          alt='Top view'
                          style={{ 
                            width: '100%', 
                            aspectRatio: '1',
                            objectFit: 'cover',
                            borderRadius: '8px',
                            border: '1px solid #e5e7eb',
                            cursor: 'pointer',
                            transition: 'opacity 0.2s'
                          }}
                          className='hover:opacity-80'
                        />
                      </Link>
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Link
                      to={`/listing/${pedal.id}`}
                      onClick={saveScrollState}
                      style={{
                        backgroundColor: '#C9A227',
                        borderRadius: '9999px',
                        border: 'none',
                        padding: '8px 20px',
                        fontFamily: 'Nunito, sans-serif',
                        color: 'white',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        fontSize: '15px',
                        textDecoration: 'none',
                        textAlign: 'center',
                        display: 'inline-block'
                      }}
                      className='hover:opacity-80 transition-opacity'
                    >
                      View Details
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        
        {/* Fixed gap below content */}
        <div style={{ height: '200px' }}></div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PublicPedalboard;

